import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ROICalculator from "@/components/ROICalculator";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, CreditCard, Heart, ShoppingBag, Headphones, Shield, CheckCircle, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import fintechHero from "@/assets/fintech-hero-v2.jpg";
import telecomHero from "@/assets/telecom-hero-v2.jpg";
import healthcareHero from "@/assets/healthcare-hero-v2.jpg";
import ecommerceHero from "@/assets/ecommerce-hero-v2.jpg";
import bpoHero from "@/assets/bpo-hero-v2.jpg";

const Industries = () => {
  const industries = [
    {
      icon: CreditCard,
      name: "Fintech & Banking",
      image: fintechHero,
      challenge: "Regulatory compliance, sensitive data handling, complex queries requiring precision and security. High call volumes for transactions, fraud detection, and account management.",
      solution: "GDPR and PCI-DSS compliant voice AI for account balance checks, transaction history, fraud alerts, payment processing, and loan inquiries with bank-grade encryption.",
      outcome: "Enhanced security, 24/7 availability, reduced operational costs while maintaining strict compliance",
      complianceBadges: ["GDPR", "PCI-DSS", "ISO 27001"],
      beforeAfter: [
        { problem: "Long wait times for account inquiries", solution: "Instant 24/7 automated responses", improvement: "-95% wait time" },
        { problem: "High fraud detection costs", solution: "Real-time AI-powered fraud analysis", improvement: "-60% fraud costs" },
        { problem: "Manual compliance monitoring", solution: "Automated compliance verification", improvement: "100% audit trail" },
      ],
    },
    {
      icon: Phone,
      name: "Telecommunications",
      image: telecomHero,
      challenge: "Massive call volumes for billing, technical support, plan changes, and account management. Complex routing and extremely long wait times leading to customer churn.",
      solution: "Automated voice agents handle account inquiries, troubleshooting, plan modifications, billing questions, service activations with intelligent escalation to specialists.",
      outcome: "45% reduction in call center costs, 90% first-call resolution, zero wait times, improved NPS",
      complianceBadges: ["GDPR", "SOC 2"],
      beforeAfter: [
        { problem: "2-hour average wait times", solution: "Instant response, zero wait", improvement: "0s wait time" },
        { problem: "Inconsistent technical support", solution: "Standardized expert-level troubleshooting", improvement: "+90% FCR" },
        { problem: "Limited service hours", solution: "24/7/365 availability", improvement: "24/7 uptime" },
      ],
    },
    {
      icon: Heart,
      name: "Healthcare",
      image: healthcareHero,
      challenge: "Appointment scheduling, prescription refills, patient follow-ups, insurance verification—all while maintaining HIPAA compliance and handling sensitive health information.",
      solution: "HIPAA-compliant voice agents for appointment booking, medication reminders, post-visit check-ins, test result notifications, and health information with secure authentication.",
      outcome: "Improved patient satisfaction, 40% reduction in no-shows, compliant automated operations, better care coordination",
      complianceBadges: ["HIPAA", "GDPR", "ISO 27001"],
      beforeAfter: [
        { problem: "Phone tag for appointments", solution: "Automated scheduling 24/7", improvement: "-40% no-shows" },
        { problem: "Missed medication reminders", solution: "Proactive AI-powered reminders", improvement: "+85% adherence" },
        { problem: "Manual insurance verification", solution: "Instant automated verification", improvement: "<10s verification" },
      ],
    },
    {
      icon: ShoppingBag,
      name: "E-commerce & Retail",
      image: ecommerceHero,
      challenge: "Order tracking, returns processing, product inquiries, cart abandonment recovery—missed revenue opportunities from slow or unavailable customer service.",
      solution: "Proactive voice outreach for order updates, personalized product recommendations, automated returns processing, cart recovery campaigns with dynamic offers.",
      outcome: "25% increase in conversions, higher customer lifetime value, 60% reduction in cart abandonment, faster order resolution",
      complianceBadges: ["GDPR", "PCI-DSS"],
      beforeAfter: [
        { problem: "60% cart abandonment rate", solution: "Proactive recovery calls, -35% abandonment", improvement: "-35% abandonment" },
        { problem: "Manual order status inquiries", solution: "Automated proactive updates", improvement: "100% automated" },
        { problem: "Limited support hours", solution: "24/7 instant assistance", improvement: "+25% conversions" },
      ],
    },
    {
      icon: Headphones,
      name: "BPO & Call Centers",
      image: bpoHero,
      challenge: "Labor-intensive operations, high agent turnover (30-40%), expensive training costs, and quality consistency issues across hundreds of agents.",
      solution: "Scalable AI voice agents that handle tier-1 support, qualify leads, appointment setting, surveys, and seamlessly escalate complex cases to human specialists.",
      outcome: "40% cost savings, consistent quality, unlimited scalability without hiring, improved agent satisfaction by removing repetitive tasks",
      complianceBadges: ["ISO 27001", "SOC 2", "GDPR"],
      beforeAfter: [
        { problem: "40% annual agent turnover", solution: "Zero AI agent turnover", improvement: "0% turnover" },
        { problem: "8-week training per agent", solution: "Instant deployment, continuous learning", improvement: "<24h deployment" },
        { problem: "Limited capacity scaling", solution: "Unlimited concurrent calls", improvement: "∞ scalability" },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 gradient-brand-text tracking-tight">Industries We Serve</h1>
            <p className="text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              Tailored AI voice solutions for the unique challenges and compliance requirements of your industry
            </p>
          </motion.div>

          <div className="space-y-20 max-w-7xl mx-auto">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="overflow-hidden glass-effect border-primary/30 hover:shadow-2xl hover-lift transition-all duration-500">
                  {/* Industry Hero Image */}
                  <div className="relative h-[28rem] overflow-hidden">
                    <motion.img 
                      whileHover={{ scale: 1.08 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                      src={industry.image} 
                      alt={industry.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10" />
                    <div className="absolute bottom-10 left-10 right-10">
                      <div className="flex items-center gap-6 mb-6">
                        <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-2xl glow-effect">
                          <industry.icon className="w-12 h-12 text-white" />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{industry.name}</h2>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {industry.complianceBadges.map((badge, idx) => (
                          <motion.span
                            key={idx}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            whileHover={{ scale: 1.1, y: -2 }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/30 border-2 border-green-500/50 text-green-300 text-sm font-bold backdrop-blur-sm shadow-lg shadow-green-500/20"
                          >
                            <Shield className="w-4 h-4" />
                            {badge}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Industry Content */}
                  <div className="p-10 md:p-12 space-y-8">
                    <div>
                      <h3 className="text-sm font-semibold text-red-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center">❌</span>
                        Challenge
                      </h3>
                      <p className="text-muted-foreground leading-relaxed text-lg">{industry.challenge}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-primary uppercase tracking-wide mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">💡</span>
                        Holionix Solution
                      </h3>
                      <p className="text-muted-foreground leading-relaxed text-lg">{industry.solution}</p>
                    </div>

                    <div className="pt-8">
                      <BeforeAfterSlider items={industry.beforeAfter} />
                    </div>
                    
                    <div className="pt-10 mt-8 border-t border-border">
                      <div className="flex items-start gap-4 mb-8 p-6 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30">
                        <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center shrink-0">
                          <TrendingUp className="w-6 h-6 text-green-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-green-400 mb-3 text-xl">Business Impact</h3>
                          <p className="text-muted-foreground text-lg leading-relaxed">{industry.outcome}</p>
                        </div>
                      </div>
                    </div>
                    
                    {index === 0 && (
                      <div className="mt-12 pt-10 border-t border-border">
                        <ROICalculator />
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <Link to="/contact" className="flex-1">
                        <Button className="w-full gradient-brand text-white hover:opacity-90 text-lg py-6">
                          Schedule Industry Demo
                        </Button>
                      </Link>
                      <Link to="/solutions" className="flex-1">
                        <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white text-lg py-6">
                          View Solutions
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <Card className="p-12 glass-effect border-primary/20 bg-gradient-to-br from-primary/10 to-accent/10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Don't See Your Industry?</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Our platform is highly customizable for any industry. Let's discuss your specific needs.
              </p>
              <Link to="/contact">
                <Button size="lg" className="gradient-brand text-white hover:opacity-90">
                  Talk to Our Team
                </Button>
              </Link>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Industries;